import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { invoicesApi } from '../../api/invoices';
import { personsApi } from '../../api/persons';
import { taxRatesApi } from '../../api/taxRates';
import { accountsApi } from '../../api/accounts';
import { cround, formatMoney } from '../../utils/formatMoney';
import { today } from '../../utils/formatDate';
import DatePickerInput from '../../components/DatePickerInput';
import type { CreateInvoiceLineRequest, CreateCreditNoteRequest, InvoiceDto } from '../../types/invoice';
import { useDocumentSerialPreview } from '../../hooks/useDocumentSerial';

interface LineForm {
  name: string;
  unit: string;
  account: string;
  vatAccount: string;
  unitPrice: string;
  quantity: string;
  vatRateId: string;
}

const emptyLine = (): LineForm => ({
  name: '',
  unit: 'vnt.',
  account: '',
  vatAccount: '',
  unitPrice: '0',
  quantity: '1',
  vatRateId: '',
});

export default function CreditNoteCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Pre-fill from linked invoice passed via route state or search param
  const prefillInvoice = (location.state as { invoice?: InvoiceDto } | null)?.invoice;
  const linkedIdParam = searchParams.get('linkedInvoiceId');
  const linkedDirection = searchParams.get('direction') ?? 'Made';

  const [date, setDate] = useState(today());
  const [documentNumber, setDocumentNumber] = useState('');
  const [content, setContent] = useState('');
  const [personId, setPersonId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [currencyRate, setCurrencyRate] = useState('1');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [linkedInvoiceId, setLinkedInvoiceId] = useState<number | null>(null);
  const [linkedInvoiceSearch, setLinkedInvoiceSearch] = useState('');
  const [invoiceDirection] = useState<'Made' | 'Received'>(linkedDirection as 'Made' | 'Received');
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [error, setError] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  // Auto-numbering from document serial
  const { previewNumber, autoSerialCode, autoFilled, setAutoFilled } = useDocumentSerialPreview('SalesInvoice', date);

  // Auto-fill document number from serial
  useEffect(() => {
    if (previewNumber && !documentNumber && !autoFilled) {
      setDocumentNumber(previewNumber);
      setAutoFilled(true);
    }
  }, [previewNumber, documentNumber, autoFilled, setAutoFilled]);

  // Fetch linked invoice if provided via search param
  const { data: fetchedLinkedInvoice } = useQuery({
    queryKey: ['invoice-detail', linkedIdParam, linkedDirection],
    queryFn: () => invoicesApi.getById(Number(linkedIdParam), linkedDirection),
    enabled: !!linkedIdParam && !prefilled,
  });

  // Search invoices for linking
  const { data: invoiceSearchResults } = useQuery({
    queryKey: ['invoices-search-credit-note', linkedInvoiceSearch, invoiceDirection],
    queryFn: () => invoicesApi.list({
      direction: invoiceDirection,
      q: linkedInvoiceSearch,
      limit: 20,
    }),
    enabled: linkedInvoiceSearch.length >= 1,
  });

  // Once we have a linked invoice (from state or fetch), prefill the form
  const linkedInvoice = prefillInvoice ?? fetchedLinkedInvoice;
  useEffect(() => {
    if (linkedInvoice && !prefilled) {
      setPersonId(String(linkedInvoice.personId));
      setCurrencyCode(linkedInvoice.currencyCode);
      setCurrencyRate(String(linkedInvoice.currencyRate));
      setInvoiceDate(linkedInvoice.date);
      setLinkedInvoiceId(linkedInvoice.id);
      setContent(
        linkedInvoice.content
          ? `${t('invoices.creditNoteFor')} ${linkedInvoice.documentNumber}: ${linkedInvoice.content}`
          : `${t('invoices.creditNoteFor')} ${linkedInvoice.documentNumber}`
      );

      // Pre-fill lines from the original invoice (same items)
      if (linkedInvoice.lines && linkedInvoice.lines.length > 0) {
        setLines(linkedInvoice.lines.map((l) => ({
          name: l.name,
          unit: l.unit,
          account: String(l.account),
          vatAccount: l.vatAccount ? String(l.vatAccount) : '',
          unitPrice: String(l.unitPrice),
          quantity: String(l.quantity),
          vatRateId: '',
        })));
      }

      setPrefilled(true);
      setLinkedInvoiceSearch(
        `${linkedInvoice.documentNumber} - ${linkedInvoice.personName ?? ''}`
      );
    }
  }, [linkedInvoice, prefilled, t]);

  // If linkedInvoiceId was in the URL but not yet set (before fetch completes)
  useEffect(() => {
    if (linkedIdParam && !linkedInvoiceId && !prefillInvoice) {
      setLinkedInvoiceId(Number(linkedIdParam));
    }
  }, [linkedIdParam, linkedInvoiceId, prefillInvoice]);

  const { data: persons } = useQuery({
    queryKey: ['persons-all'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: taxRates } = useQuery({
    queryKey: ['tax-rates-all'],
    queryFn: taxRatesApi.list,
  });

  const createMutation = useMutation({
    mutationFn: invoicesApi.createCreditNote,
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/${id}?direction=Made`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateLine = (index: number, field: keyof LineForm, value: string) => {
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current) {
        next[index] = { ...current, [field]: value } as LineForm;
      }
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const getTaxRate = (id: string) => {
    if (!id) return 0;
    return taxRates?.find((r) => r.id === Number(id))?.rate ?? 0;
  };

  const buildLineRequest = (line: LineForm): CreateInvoiceLineRequest => {
    const unitPrice = parseFloat(line.unitPrice) || 0;
    const quantity = parseFloat(line.quantity) || 0;
    const vatRate = getTaxRate(line.vatRateId);
    const sum = cround(unitPrice * quantity);
    const sumVat = cround(sum * vatRate);

    return {
      name: line.name,
      unit: line.unit || 'vnt.',
      account: parseInt(line.account) || 0,
      vatAccount: line.vatAccount ? parseInt(line.vatAccount) : null,
      unitPrice,
      quantity,
      vatRate,
      sum,
      sumVat,
      sumOriginal: sum,
      sumVatOriginal: sumVat,
      includeVatInObject: false,
      vatIsVirtual: false,
      declarationSchemaId: 0,
    };
  };

  const handleSelectLinkedInvoice = (invoice: InvoiceDto) => {
    setLinkedInvoiceId(invoice.id);
    setPersonId(String(invoice.personId));
    setCurrencyCode(invoice.currencyCode);
    setCurrencyRate(String(invoice.currencyRate));
    setInvoiceDate(invoice.date);
    setContent(
      invoice.content
        ? `${t('invoices.creditNoteFor')} ${invoice.documentNumber}: ${invoice.content}`
        : `${t('invoices.creditNoteFor')} ${invoice.documentNumber}`
    );
    setLinkedInvoiceSearch(`${invoice.documentNumber} - ${invoice.personName ?? ''}`);

    if (invoice.lines && invoice.lines.length > 0) {
      setLines(invoice.lines.map((l) => ({
        name: l.name,
        unit: l.unit,
        account: String(l.account),
        vatAccount: l.vatAccount ? String(l.vatAccount) : '',
        unitPrice: String(l.unitPrice),
        quantity: String(l.quantity),
        vatRateId: '',
      })));
    }
  };

  const handleClearLinkedInvoice = () => {
    setLinkedInvoiceId(null);
    setPersonId('');
    setContent('');
    setInvoiceDate('');
    setCurrencyCode('EUR');
    setCurrencyRate('1');
    setLines([emptyLine()]);
    setLinkedInvoiceSearch('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const explicitNumber = documentNumber.trim();
    const hasSerial = !!autoSerialCode;
    if (!explicitNumber && !hasSerial) {
      setError('Document number is required. Configure a serial in Settings or enter manually.');
      return;
    }
    if (!content.trim()) {
      setError('Content is required');
      return;
    }
    if (!personId) {
      setError('Person is required');
      return;
    }
    if (!linkedInvoiceId) {
      setError('A linked invoice is required for a credit note');
      return;
    }

    const lineRequests = lines.map(buildLineRequest);
    const invalidLine = lineRequests.find((l) => !l.account || !l.name);
    if (invalidLine) {
      setError('Each line must have a name and account');
      return;
    }

    const payload: CreateCreditNoteRequest = {
      date,
      documentNumber: explicitNumber,
      content,
      personId: parseInt(personId),
      currencyCode,
      currencyRate: parseFloat(currencyRate) || 1,
      lines: lineRequests,
      autoSerialCode: !explicitNumber && autoSerialCode ? autoSerialCode : undefined,
      invoiceDate: invoiceDate || undefined,
      linkedInvoiceId,
    };

    createMutation.mutate(payload);
  };

  // Compute totals for display
  const computedLines = lines.map(buildLineRequest);
  const totalSum = computedLines.reduce((acc, l) => acc + l.sum, 0);
  const totalVat = computedLines.reduce((acc, l) => acc + l.sumVat, 0);

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">
          {t('invoices.creditNoteTitle')}
        </h1>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          onClick={() => navigate(-1)}
        >
          {t('common.cancel')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Linked invoice search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="font-semibold text-sm mb-3">
            {t('invoices.linkedInvoice')}
          </h2>

          {linkedInvoiceId ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 px-3 py-2 bg-gray-50 rounded-md text-sm border border-gray-200">
                <span className="font-medium">{linkedInvoiceSearch}</span>
                {linkedInvoice && (
                  <span className="text-gray-500 ml-2">
                    {formatMoney(linkedInvoice.totalSum + linkedInvoice.totalVat)} {linkedInvoice.currencyCode}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                onClick={handleClearLinkedInvoice}
              >
                {t('invoices.unlinkInvoice')}
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                placeholder={t('invoices.searchInvoicePlaceholder')}
                value={linkedInvoiceSearch}
                onChange={(e) => setLinkedInvoiceSearch(e.target.value)}
                autoComplete="off"
              />
              {invoiceSearchResults && invoiceSearchResults.length > 0 && linkedInvoiceSearch && !linkedInvoiceId && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {invoiceSearchResults.map((inv) => (
                    <button
                      key={inv.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      onClick={() => handleSelectLinkedInvoice(inv)}
                    >
                      <div className="font-medium">{inv.documentNumber}</div>
                      <div className="text-gray-500 text-xs">
                        {inv.personName} | {inv.date} | {formatMoney(inv.totalSum + inv.totalVat)} {inv.currencyCode}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Credit note details */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DatePickerInput
              label={t('common.date')}
              value={date}
              onChange={setDate}
              required
            />
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('invoices.documentNumber')}</label>
              <input
                type="text"
                className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md ${!documentNumber && previewNumber ? 'placeholder:text-gray-400' : ''}`}
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder={previewNumber || 'e.g. KF0001'}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('invoices.invoiceDate')}</label>
              <input
                type="date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('invoices.client')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                required
              >
                <option value="">-- {t('invoices.selectPerson')} --</option>
                {persons?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.currency')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('invoices.currencyRate')}</label>
              <input
                type="number"
                step="0.0001"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyRate}
                onChange={(e) => setCurrencyRate(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">{t('common.content')}</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('invoices.creditNoteContentPlaceholder')}
                required
              />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-sm">{t('invoices.lineItems')}</h2>
            <button
              type="button"
              className="px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
              onClick={addLine}
            >
              + {t('invoices.addLine')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left min-w-32">{t('common.name')}</th>
                  <th className="text-left w-20">{t('invoices.unit')}</th>
                  <th className="text-right w-20">{t('common.account')}</th>
                  <th className="text-right w-20">{t('invoices.price')}</th>
                  <th className="text-right w-16">{t('invoices.qty')}</th>
                  <th className="text-left w-28">{t('invoices.vatRate')}</th>
                  <th className="text-right w-20">{t('invoices.sum')}</th>
                  <th className="text-right w-20">{t('invoices.vat')}</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const built = buildLineRequest(line);
                  return (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.name}
                          onChange={(e) => updateLine(i, 'name', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.unit}
                          onChange={(e) => updateLine(i, 'unit', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.account}
                          onChange={(e) => updateLine(i, 'account', e.target.value)}
                          required
                        >
                          <option value="">--</option>
                          {accounts?.map((a) => (
                            <option key={a.accountNumber} value={a.accountNumber}>
                              {a.accountNumber} {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.unitPrice}
                          onChange={(e) => updateLine(i, 'unitPrice', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.quantity}
                          onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.vatRateId}
                          onChange={(e) => updateLine(i, 'vatRateId', e.target.value)}
                        >
                          <option value="">{t('invoices.none')}</option>
                          {taxRates?.filter((r) => !r.isObsolete).map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} ({(r.rate * 100).toFixed(0)}%)
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="num">{formatMoney(built.sum, false)}</td>
                      <td className="num">{formatMoney(built.sumVat, false)}</td>
                      <td>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeLine(i)}
                          disabled={lines.length <= 1}
                        >
                          x
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="text-right font-semibold">{t('common.total')}</td>
                  <td className="num font-semibold">{formatMoney(totalSum)}</td>
                  <td className="num font-semibold">{formatMoney(totalVat)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={6} className="text-right font-bold">{t('invoices.totalWithVat')}</td>
                  <td colSpan={2} className="num font-bold text-primary-700">{formatMoney(totalSum + totalVat)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isPending ? t('invoices.creatingCreditNote') : t('invoices.createCreditNote')}
          </button>
        </div>
      </form>
    </div>
  );
}
